import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { popupApi } from '@/lib/api-client';
import { createDefaultDocument } from '@acorn/popup-core';
import { toast } from 'sonner';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import type { PopupStatus } from '@/types/popups';

export const Route = createFileRoute('/')({
  component: PopupsListPage,
});

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 4px;
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const CreateButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #111827;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  border-radius: 24px;
  border: none;
  cursor: pointer;
  transition: background 120ms;

  &:hover {
    background: #1f2937;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TableCard = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
`;

const TableToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  gap: 12px;
`;

const SearchInput = styled.input`
  width: 320px;
  padding: 10px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #111827;
  }
`;

const StatusSelect = styled.select`
  padding: 10px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  cursor: pointer;
  background: #fff;

  &:focus {
    border-color: #111827;
  }
`;

const EmptyState = styled.div`
  padding: 64px 24px;
  text-align: center;
  color: #6b7280;
`;

const EmptyTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px;
`;

const EmptyMessage = styled.p`
  font-size: 14px;
  margin: 0 0 24px;
`;

const LoadingMessage = styled.div`
  padding: 24px;
  color: #9ca3af;
  font-size: 14px;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 24px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e5e7eb;
`;

const Tr = styled.tr`
  cursor: pointer;

  &:hover td {
    background: #f9fafb;
  }
`;

const Td = styled.td`
  padding: 16px 24px;
  font-size: 14px;
  border-bottom: 1px solid #f3f4f6;
`;

const TdMuted = styled(Td)`
  color: #6b7280;
`;

const TdAction = styled(Td)`
  width: 48px;
`;

const TdName = styled(Td)`
  font-weight: 500;
  color: #111827;
`;

interface StatusBadgeProps {
  $bg: string;
  $fg: string;
}

const StatusBadge = styled.span<StatusBadgeProps>`
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  padding: 4px;
  font-size: 14px;
  line-height: 1;
  border-radius: 4px;

  &:hover {
    color: #ef4444;
    background: #fee2e2;
  }
`;

const statusColor: Record<string, { bg: string; fg: string }> = {
  active: { bg: '#dcfce7', fg: '#166534' },
  draft: { bg: '#f3f4f6', fg: '#374151' },
  paused: { bg: '#fef9c3', fg: '#854d0e' },
  archived: { bg: '#fee2e2', fg: '#991b1b' },
};

const defaultStatusColor = statusColor.draft;

function PopupsListPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['popups', statusFilter],
    queryFn: () => popupApi.list(statusFilter || undefined),
  });

  const createPopup = useMutation({
    mutationFn: () => popupApi.create({
      name: 'Untitled Popup',
      document: createDefaultDocument(),
    }),
    onSuccess: (popup) => {
      queryClient.invalidateQueries({ queryKey: ['popups'] });
      navigate({ to: '/popups/$popupId/edit', params: { popupId: popup.id } });
    },
    onError: () => toast.error('Failed to create popup'),
  });

  const deletePopup = useMutation({
    mutationFn: (id: string) => popupApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['popups'] });
      toast.success('Popup deleted');
    },
    onError: () => toast.error('Failed to delete popup'),
  });

  const popups = data?.items ?? [];

  const filtered = useMemo(() => {
    if (!search) return popups;
    const q = search.toLowerCase();
    return popups.filter((p) => p.name.toLowerCase().includes(q));
  }, [popups, search]);

  return (
    <div>
      <PageHeader>
        <div>
          <PageTitle>Popups</PageTitle>
          <PageSubtitle>Create and manage opt-in popups</PageSubtitle>
        </div>
        <CreateButton
          onClick={() => createPopup.mutate()}
          disabled={createPopup.isPending}
        >
          + Create Popup
        </CreateButton>
      </PageHeader>

      <TableCard>
        <TableToolbar>
          <SearchInput
            placeholder="Search popups..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <StatusSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="archived">Archived</option>
          </StatusSelect>
        </TableToolbar>

        {isLoading ? (
          <LoadingMessage>Loading popups...</LoadingMessage>
        ) : filtered.length === 0 ? (
          <EmptyState>
            <EmptyTitle>
              {search ? 'No matching popups' : 'No popups yet'}
            </EmptyTitle>
            <EmptyMessage>
              {search ? 'Try a different search term' : 'Create your first popup to get started'}
            </EmptyMessage>
          </EmptyState>
        ) : (
          <StyledTable>
            <thead>
              <tr>
                {['Name', 'Status', 'Version', 'Updated', ''].map((h) => (
                  <Th key={h}>{h}</Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((popup) => (
                <Tr
                  key={popup.id}
                  onClick={() => navigate({ to: '/popups/$popupId/edit', params: { popupId: popup.id } })}
                >
                  <TdName>{popup.name}</TdName>
                  <Td>
                    <StatusBadge
                      $bg={(statusColor[popup.status] ?? defaultStatusColor).bg}
                      $fg={(statusColor[popup.status] ?? defaultStatusColor).fg}
                    >
                      {popup.status}
                    </StatusBadge>
                  </Td>
                  <Td>v{popup.published_version}</Td>
                  <TdMuted>{formatDate(popup.updated_at)}</TdMuted>
                  <TdAction>
                    <DeleteButton
                      aria-label={`Delete popup ${popup.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Delete "${popup.name}"?`)) {
                          deletePopup.mutate(popup.id);
                        }
                      }}
                      title="Delete"
                    >
                      &#x2715;
                    </DeleteButton>
                  </TdAction>
                </Tr>
              ))}
            </tbody>
          </StyledTable>
        )}
      </TableCard>
    </div>
  );
}
